import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { FORMATIONS } from '@shared/formations';
import { dirFor, type Locale } from '@/i18n';
import { useOrg } from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate, sheetName } from '@/lib/format';
import { buildSquadSheet, type BuiltSquadSheet, type SheetPlayer } from '@/lib/squadSheet';
import { drawSquadSheet } from '@/diagrams/squadCanvas';
import {
  PAGE_HEIGHT_PX,
  PAGE_WIDTH_PX,
  exportCanvasPagesPdf,
  printOnly,
  reportFilename,
} from '@/reports/export';
import { Button } from '@/ui/Button';
import { Notice } from '@/ui/Panel';
import { SelectInput } from '@/ui/Field';
import { Section } from '@/ui/Section';

export interface SheetPeriod {
  fromMinute: number;
  toMinute: number;
  formation: string;
  players: SheetPlayer[];
}

/**
 * One pitch per period, one page each.
 *
 * A minutes plan is read on the touchline, one window at a time — "who is on
 * now" is a question about a shape, not about a column of a table. So each
 * period gets a page of its own with the same header above it and its own
 * window as the title, drawn by the same renderer as the squad sheet so the two
 * read as the same picture.
 *
 * Players take their place from the position they play. A coach who wants
 * somebody somewhere else moves him on the Positions screen, which is where that
 * decision already lives.
 */
export function PeriodSheets({
  periods,
  fixture,
  teamName,
  locale,
  onFormationChange,
}: {
  periods: SheetPeriod[];
  fixture: { opponent?: string | null; onDate: string; kickOff?: string | null };
  teamName?: string;
  locale: Locale;
  onFormationChange: (index: number, formation: string) => void;
}) {
  const t = useTranslations();
  const org = useOrg();
  const { user } = useAuthSession();
  const holder = useRef<HTMLDivElement>(null);
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sheets = useMemo(
    () =>
      periods.map((period) => {
        const built = fillEmptySlots(buildSquadSheet(period.formation, period.players));
        return {
          slots: built.slots.map((s) => ({
            positionKey: s.positionKey,
            x: s.x,
            y: s.y,
            unit: s.unit,
            players: s.players.map((p) => ({
              name: p.name,
              shirtNumber: p.shirtNumber,
              roleKey: p.roleKey,
            })),
          })),
          unplaced: built.unplaced.map((p) => ({
            name: p.name,
            shirtNumber: p.shirtNumber,
            positionKey: p.primaryPositionKey,
          })),
          unplacedLabel: t('minutes.alsoOn'),
          header: {
            // The window is the title; everything else is the same on every page,
            // because a sheet that leaves the folder has to say whose it is.
            title: `${period.fromMinute}–${period.toMinute}′`,
            phaseLabel: [org.data?.name, t('minutes.reportTitle')].filter(Boolean).join(' · '),
            teamName: [teamName, fixture.opponent ? `${t('match.versus')} ${fixture.opponent}` : '']
              .filter(Boolean)
              .join(' · '),
            coachName: user?.name ?? '',
            formationKey: period.formation,
            date: [formatDate(fixture.onDate, locale), fixture.kickOff]
              .filter(Boolean)
              .join(' · '),
          },
        };
      }),
    [periods, org.data?.name, teamName, fixture, user?.name, locale, t],
  );

  useEffect(() => {
    sheets.forEach((sheet, i) => {
      const ctx = canvases.current[i]?.getContext('2d');
      if (ctx) drawSquadSheet(ctx, PAGE_WIDTH_PX, PAGE_HEIGHT_PX, sheet, dirFor(locale));
    });
  }, [sheets, locale]);

  const runExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const pages = canvases.current.filter((c): c is HTMLCanvasElement => Boolean(c));
      await exportCanvasPagesPdf(
        pages,
        reportFilename(
          [t('minutes.reportTitle'), teamName, fixture.opponent],
          fixture.onDate,
        ),
      );
    } catch {
      setError(t('report.failed'));
    } finally {
      setExporting(false);
    }
  };

  if (periods.length === 0) return null;

  return (
    <Section title={t('minutes.sheets')} hint={t('minutes.sheetsHint')}>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" busy={exporting} onClick={() => void runExport()}>
          {exporting ? t('report.preparing') : t('minutes.exportPages', { count: periods.length })}
        </Button>
        <Button variant="quiet" onClick={() => holder.current && printOnly(holder.current)}>
          {t('report.print')}
        </Button>
      </div>
      {error ? (
        <div className="mt-3">
          <Notice tone="risk">{error}</Notice>
        </div>
      ) : null}

      <div ref={holder} className="report-preview mt-4 grid gap-5">
        {periods.map((period, i) => (
          <div key={i} className="report-sheet-page grid gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-meta font-semibold">
                {period.fromMinute}–{period.toMinute}&rsquo;
              </span>
              <div className="w-32">
                <SelectInput
                  aria-label={t('depth.formation')}
                  value={period.formation}
                  onChange={(e) => onFormationChange(i, e.target.value)}
                >
                  {FORMATIONS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.key}
                    </option>
                  ))}
                </SelectInput>
              </div>
              <span className="text-meta text-ink-muted">
                {t('minutes.onPitch', { count: period.players.length })}
              </span>
            </div>
            <canvas
              ref={(el) => {
                canvases.current[i] = el;
              }}
              width={PAGE_WIDTH_PX}
              height={PAGE_HEIGHT_PX}
              className="mx-auto block h-auto w-full max-w-[46rem] rounded-(--radius-panel) border border-line bg-paper"
            />
          </div>
        ))}
      </div>
    </Section>
  );
}

/**
 * Puts whoever is left into whatever slots are empty.
 *
 * `buildSquadSheet` places a player by the position he plays, and a squad that
 * has not had its positions filled in yet places nobody — which produced a pitch
 * of empty shirts and eleven names underneath it. A coach looking at that would
 * fix it by dragging them onto the shape, so the sheet does it for him and he
 * corrects the ones that are wrong. The alternative is a blank page, and a blank
 * page is not more honest, it is just less use.
 */
function fillEmptySlots(built: BuiltSquadSheet): BuiltSquadSheet {
  if (built.unplaced.length === 0) return built;
  const spare = [...built.unplaced];
  const slots = built.slots.map((slot) => {
    if (slot.players.length > 0 || spare.length === 0) return slot;
    return { ...slot, players: [spare.shift()!] };
  });
  return { slots, unplaced: spare };
}

/**
 * The squad rows the sheet needs, from whatever the screen is holding.
 *
 * Takes the period's own assignment — a player and where the coach put him in
 * *this* window — and falls back to the position the player normally plays. A
 * full back pushed to the wing for the last twenty minutes is the whole reason
 * periods exist; reading his squad position instead printed him at left back
 * on every sheet, and the coach's change was nowhere on the page.
 */
export function toSheetPlayers(
  assigned: Iterable<readonly [string, string | null | undefined]>,
  squad: readonly {
    playerId: string;
    firstName: string;
    lastName: string;
    shirtNumber?: number | null;
    positionKey?: string | null;
  }[],
): SheetPlayer[] {
  const byId = new Map(squad.map((p) => [p.playerId, p]));
  return [...assigned].flatMap(([id, positionKey]) => {
    const p = byId.get(id);
    if (!p) return [];
    return [
      {
        id: p.playerId,
        name: sheetName(p.firstName, p.lastName),
        shirtNumber: p.shirtNumber,
        primaryPositionKey: positionKey ?? p.positionKey,
      },
    ];
  });
}
